import { ChangeDetectionStrategy, Component, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { FileSizePipe, TruncatePipe } from '@dss-libs/shared/ui/common-pipes';
import { SnackbarService } from '@dss-libs/shared/utility/common-services';
import { EventTypeEnum } from '@dss-libs/shared/type/common-types';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { FormsModule } from '@angular/forms';
import { DragAndDropDirective } from '@dss-libs/shared/ui/common-directives';
import { IUploadedFileModel } from '@dss-libs/shared/type/common-types';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { ButtonComponent } from '../button/button.component';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';

@Component({
  selector: 'dss-libs-ui-upload-file',
  imports: [
    TranslocoDirective,
    FileSizePipe,
    BaseModalComponent,
    ButtonComponent,
    FormsModule,
    DragAndDropDirective,
    TruncatePipe,
    MatIcon,
    TranslocoDecimalPipe,
  ],
  templateUrl: './upload-file.component.html',
  styleUrl: './upload-file.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadFileComponent {
  readonly filesUploadedListEvent = output<FormData>();
  filesUploadedList: FormData = new FormData();
  uploadedFiles: Array<IUploadedFileModel> = [];
  files: File[] = [];
  readonly fileInput = viewChild.required<ElementRef>('fileInput');
  isRenameModalOpen = false;
  duplicateFiles: Array<File> = [];
  FileReader: FileReader = new FileReader();
  newFileNames: Array<string> = [];

  dropzoneHasError = input(false);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translocoService = inject(TranslocoService);

  handleFile(fileInput: any) {
    this.files = fileInput.files || fileInput;
    if (this.files && this.files.length) {
      for (const key in this.files) {
        if (Object.prototype.hasOwnProperty.call(this.files, key)) {
          const file: File = this.files[key];
          if (this.extractFileExtension(file.name).toLowerCase() !== '.pdf') {
            this.snackbarService.showToaster(EventTypeEnum.ERROR, 'ERRORS.FILE_NOT_SUPPORTED');
            continue;
          }
          if (this.filesUploadedList.has(`${file.name}`)) {
            this.isRenameModalOpen = true;
            this.duplicateFiles.push(file);
          } else {
            this.uploadFiles(file);
          }
        }
      }
    }
    this.resetFileInputUiValue();
  }

  removeFile(idx: number, fileName: string) {
    for (const [, value] of this.filesUploadedList as any) {
      if (value.name == fileName) {
        this.filesUploadedList.delete(fileName);
      }
    }
    this.uploadedFiles = this.uploadedFiles.filter((u, index) => index !== idx);
    this.filesUploadedListEvent.emit(this.filesUploadedList);
  }

  resetFileInputUiValue(): void {
    // Helps to detect change in case a document were uploaded twice in a row
    this.fileInput().nativeElement.value = '';
  }

  uploadFiles(file: File) {
    this.FileReader = new FileReader();
    this.FileReader.readAsDataURL(file);
    this.FileReader.onload = (event: any) => {
      this.uploadedFiles.push({
        FileName: file.name,
        FileSizeKb: file.size / 1024,
        FileType: file.type,
        FileUrl: event.target.result,
        extension: this.extractFileExtension(file.name),
      });
      this.filesUploadedList.append(`${file.name}`, file);
      this.filesUploadedListEvent.emit(this.filesUploadedList);
    };
  }

  uploadNewFile(file: File, fileName: string) {
    const newFile = new File([file], fileName, {
      type: file.type,
    });
    this.uploadFiles(newFile);
  }

  extractFileExtension(fileName: string): string {
    return '.' + fileName.split('.').pop();
  }

  closeModal(newFileNames: Array<string>) {
    const uniqueValues = new Set(newFileNames.map((f) => f.trim().toLowerCase()));
    if (uniqueValues.size !== newFileNames.length || newFileNames.length !== this.duplicateFiles.length) {
      return;
    }
    for (const fileName of newFileNames) {
      if (
        this.uploadedFiles.filter((f) => this.splitFileNameToLowerCase(f.FileName) === fileName.trim().toLowerCase())
          .length > 0 ||
        fileName.trim() === ''
      ) {
        return;
      }
    }
    for (let i = 0; i < this.duplicateFiles.length; i++) {
      const file = this.duplicateFiles[i];
      const newName = this.newFileNames[i];
      const fileExtension = this.extractFileExtension(file.name);
      this.uploadNewFile(this.duplicateFiles[i], newName + fileExtension);
    }
    this.newFileNames = [];
    this.duplicateFiles = [];
    this.isRenameModalOpen = false;
  }

  isFileNameUnique(fileName: string) {
    if (fileName == undefined) return true;
    const duplicateInputNames = this.newFileNames
      .map((f) => f.toLowerCase())
      .filter((f) => f === fileName.toLowerCase());
    if (duplicateInputNames.length > 1) {
      return false;
    }
    const duplicateFileName = this.uploadedFiles.some(
      (f) => f.FileName.split('.')[0].toLowerCase() === fileName.toLowerCase()
    );
    return !duplicateFileName;
  }

  clearModalData() {
    this.duplicateFiles = [];
    this.isRenameModalOpen = false;
    this.newFileNames = [];
  }

  splitFileNameToLowerCase(fileName: string) {
    return fileName.split('.')[0].toLowerCase().trim();
  }
}
